import { type ProductImage, type Product } from '@curology/product-service-sdk';

import {
  type Estimate,
  type ProductVariantProduct,
} from '@/services/resubscriptionService/types';
import {
  type PurchaseContext,
  type RxPurchaseContextPrices,
} from '@/types/purchaseContextToggle/types';

export interface ProductPriceMap {
  [key: string]: number;
}
/* eslint-disable sonarjs/no-duplicate-string */
export const filterSubscribedProducts = (
  allProducts: Product[],
  productVariants: ProductVariantProduct[],
) => {
  const subscribedSlugs = productVariants.map((variant) =>
    shouldUseProductVariant(variant.productSlug)
      ? variant.slug
      : variant.productSlug,
  );

  return allProducts
    .filter((product) => {
      // temp special handling for lip balm
      if (product.slug.includes('the-lip-balm')) {
        const lipBalmProductVariant = product.variants.find((variant) =>
          subscribedSlugs.includes(pocketdermLipbalmSlugMapping[variant.slug]),
        );
        if (lipBalmProductVariant) {
          product.slug = lipBalmProductVariant.slug;
          product.thumbnailImage =
            ProductDataMap[
              pocketdermLipbalmSlugMapping[lipBalmProductVariant.slug]
            ].thumbnailImage;
          product.name = lipBalmProductVariant.name;
          return true;
        }
        return false;
      }

      if (
        product.slug === 'the-moisturizer' ||
        product.slug === 'cream-moisturizer'
      ) {
        const moisturizerProductVariant = product.variants.find((variant) =>
          subscribedSlugs.includes(variant.slug),
        );
        if (moisturizerProductVariant) {
          product.slug = moisturizerProductVariant.slug;
          return true;
        }
        return false;
      }

      // temp special handling for Rx products
      if (product.isRx) {
        if (
          subscribedSlugs.includes(product.slug) ||
          (product.federatedIdentifiers &&
            subscribedSlugs.includes(product.federatedIdentifiers[0]))
        ) {
          product.name = ProductDataMap[product.slug].name;
          product.thumbnailImage = ProductDataMap[product.slug].thumbnailImage;
          return true;
        }
        return false;
      }

      return (
        subscribedSlugs.includes(product.slug) ||
        (product.federatedIdentifiers &&
          subscribedSlugs.includes(product.federatedIdentifiers[0]))
      );
    })
    .sort((product1, product2) => {
      if (product1.isRx && product2.isRx) {
        return product1.sortOrder && product2.sortOrder
          ? product1.sortOrder - product2.sortOrder
          : 0;
      }
      return product1.isRx ? -1 : 1;
    });
};

export const mapProductSlugsToPrices = (
  filteredProducts: Product[],
  productVariants: ProductVariantProduct[],
) => {
  const productPriceMap: ProductPriceMap = {};

  filteredProducts.forEach((product) => {
    const subscribedProductVariant = productVariants.find((variant) => {
      if (product.slug.includes('the-lip-balm')) {
        return variant.slug === pocketdermLipbalmSlugMapping[product.slug];
      }
      return (
        product.slug === variant.productSlug ||
        (product.federatedIdentifiers &&
          product.federatedIdentifiers[0] === variant.productSlug)
      );
    });

    if (subscribedProductVariant) {
      productPriceMap[product.slug] = subscribedProductVariant.price * 100;
    }
  });

  return productPriceMap;
};

// Not sure why I can't abstract this function out, but when I try it breaks LipBalm
export const mapProductSlugsToOneTimePrices = (
  filteredProducts: Product[],
  productVariants: ProductVariantProduct[],
) => {
  const productPriceMap: ProductPriceMap = {};

  filteredProducts.forEach((product) => {
    const subscribedProductVariant = productVariants.find((variant) => {
      if (product.slug.includes('the-lip-balm')) {
        return variant.slug === pocketdermLipbalmSlugMapping[product.slug];
      }
      return (
        product.slug === variant.productSlug ||
        (product.federatedIdentifiers &&
          product.federatedIdentifiers[0] === variant.productSlug)
      );
    });

    if (subscribedProductVariant) {
      productPriceMap[product.slug] =
        subscribedProductVariant.onetimePrice * 100;
    }
  });

  return productPriceMap;
};

const pocketdermLipbalmSlugMapping: {
  [key: string]: string;
} = {
  'the-lip-balm-original': 'the-lip-balm-original',
  'the-lip-balm-mint': 'the-lip-balm-mint',
  'the-lip-balm-passionfruit': 'the-lip-balm-passion-fruit',
  'the-lip-balm-vanilla': 'the-lip-balm-vanilla',
  'the-lip-balm-spf': 'the-lip-balm-spf-30',
};

export const centsToDollarsAndCents = (amountInCents: number) => {
  return (amountInCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
};

const shouldUseProductVariant = (slug: string) => {
  return slug === 'the-lip-balm' || slug === 'the-moisturizer';
};

const createPriceObjectFromEstimate = (estimate: Estimate) => ({
  adjustmentAmount: estimate.adjustmentAmount.amount,
  finalAmount: estimate.finalAmount.amount,
  originalAmount: estimate.originalAmount.amount,
});

export const getRxPurchaseContextPricesFromEstimates = (
  estimates: Estimate[],
): RxPurchaseContextPrices => {
  const rxPurchaseContextPrices: Partial<RxPurchaseContextPrices> = {};

  estimates.forEach((estimate) => {
    const contextKey =
      `${estimate.purchaseContext}ItemPrices` as keyof RxPurchaseContextPrices;
    rxPurchaseContextPrices[contextKey] =
      createPriceObjectFromEstimate(estimate);
  });

  return rxPurchaseContextPrices as RxPurchaseContextPrices;
};

export type PriceDifference = {
  context: PurchaseContext;
  difference: number;
};

export const calculatePriceDifference = (
  estimate1: Estimate,
  estimate2: Estimate,
): PriceDifference => {
  const [higherAmount, lowerAmount] = [
    estimate1.total.amount,
    estimate2.total.amount,
  ].sort((a, b) => b - a);
  const difference = higherAmount - lowerAmount;

  return {
    context: estimate1.purchaseContext,
    difference,
  };
};

export const getPurchaseContextAwareData = <T>(
  purchaseContext: PurchaseContext,
  subscriptionData: T,
  onetimeData: T,
): T => {
  switch (purchaseContext) {
    case 'onetime':
      return onetimeData;
    case 'subscription':
      return subscriptionData;
    default:
      console.error('No matching purchase context found:', purchaseContext);
      return subscriptionData;
  }
};

const PRODUCT_SLUGS = {
  customFormula: 'custom-formula',
  futureProof: 'future-proof',
  deepCleanse: 'deep-cleanse',
  bodyCleanse: 'body-cleanse',
  lipBalmOriginal: 'the-lip-balm-original',
  lipBalmMint: 'the-lip-balm-mint',
  lipBalmPassionFruit: 'the-lip-balm-passion-fruit',
  lipBalmVanilla: 'the-lip-balm-vanilla',
  lipBalmSpf: 'the-lip-balm-spf-30',
  dayPrime: 'dayprime',
};

const ProductDataMap: {
  [key: string]: {
    name: string;
    thumbnailImage: ProductImage[];
  };
} = {
  [PRODUCT_SLUGS.customFormula]: {
    name: 'Custom Formula',
    thumbnailImage: [
      {
        fileName: 'CF_Full_2x.png',
        url: 'https://images.ctfassets.net/8pzh4g8cs30t/6prM4LU3P0Nh4isoUqFrQj/537dbe5739f377221122727d0af194f1/CF_Full_2x.png',
        urlRaw:
          'https://images.ctfassets.net/8pzh4g8cs30t/6prM4LU3P0Nh4isoUqFrQj/537dbe5739f377221122727d0af194f1/CF_Full_2x.png',
        urlThumbnail:
          'https://images.ctfassets.net/8pzh4g8cs30t/6prM4LU3P0Nh4isoUqFrQj/537dbe5739f377221122727d0af194f1/CF_Full_2x.png',
        urlSmall:
          'https://images.ctfassets.net/8pzh4g8cs30t/6prM4LU3P0Nh4isoUqFrQj/537dbe5739f377221122727d0af194f1/CF_Full_2x.png',
        urlLarge:
          'https://images.ctfassets.net/8pzh4g8cs30t/6prM4LU3P0Nh4isoUqFrQj/537dbe5739f377221122727d0af194f1/CF_Full_2x.png',
      },
    ],
  },
  [PRODUCT_SLUGS.dayPrime]: {
    name: 'Day Prime',
    thumbnailImage: [
      {
        fileName: 'dayPrime.png',
        url: 'https://images.ctfassets.net/mdcr7mahi0vp/6VWdZrSHQyQZTXPRGcOv9y/e43bf6423e6cc9b191cf203f4b096b08/dayPrime.png',
        urlRaw:
          'https://images.ctfassets.net/mdcr7mahi0vp/6VWdZrSHQyQZTXPRGcOv9y/e43bf6423e6cc9b191cf203f4b096b08/dayPrime.png',
        urlThumbnail:
          'https://images.ctfassets.net/mdcr7mahi0vp/6VWdZrSHQyQZTXPRGcOv9y/e43bf6423e6cc9b191cf203f4b096b08/dayPrime.png',
        urlSmall:
          'https://images.ctfassets.net/mdcr7mahi0vp/6VWdZrSHQyQZTXPRGcOv9y/e43bf6423e6cc9b191cf203f4b096b08/dayPrime.png',
        urlLarge:
          'https://images.ctfassets.net/mdcr7mahi0vp/6VWdZrSHQyQZTXPRGcOv9y/e43bf6423e6cc9b191cf203f4b096b08/dayPrime.png',
      },
    ],
  },
  [PRODUCT_SLUGS.futureProof]: {
    name: 'Future Proof',
    thumbnailImage: [
      {
        fileName: 'FP_Full_2x.png',
        url: 'https://images.ctfassets.net/8pzh4g8cs30t/3myQWHijmnCYPA65bEs6Se/289c6cfa6f58288fb68ae21bc9dbb9b8/DC_Full_2x.png',
        urlRaw:
          'https://images.ctfassets.net/8pzh4g8cs30t/3myQWHijmnCYPA65bEs6Se/289c6cfa6f58288fb68ae21bc9dbb9b8/DC_Full_2x.png',
        urlThumbnail:
          'https://images.ctfassets.net/8pzh4g8cs30t/7IwhVIOXzCAo0T5eb01lmO/7bfcaaf4c140b2613ae86ba370433d5f/FP_Full_2x.png',
        urlSmall:
          'https://images.ctfassets.net/8pzh4g8cs30t/7IwhVIOXzCAo0T5eb01lmO/7bfcaaf4c140b2613ae86ba370433d5f/FP_Full_2x.png',
        urlLarge:
          'https://images.ctfassets.net/8pzh4g8cs30t/7IwhVIOXzCAo0T5eb01lmO/7bfcaaf4c140b2613ae86ba370433d5f/FP_Full_2x.png',
      },
    ],
  },
  [PRODUCT_SLUGS.deepCleanse]: {
    name: 'Deep Cleanse',
    thumbnailImage: [
      {
        fileName: 'DC_Full_2x.png',
        url: 'https://images.ctfassets.net/8pzh4g8cs30t/3myQWHijmnCYPA65bEs6Se/289c6cfa6f58288fb68ae21bc9dbb9b8/DC_Full_2x.png',
        urlRaw:
          'https://images.ctfassets.net/8pzh4g8cs30t/3myQWHijmnCYPA65bEs6Se/289c6cfa6f58288fb68ae21bc9dbb9b8/DC_Full_2x.png',
        urlThumbnail:
          'https://images.ctfassets.net/8pzh4g8cs30t/3myQWHijmnCYPA65bEs6Se/289c6cfa6f58288fb68ae21bc9dbb9b8/DC_Full_2x.png',
        urlSmall:
          'https://images.ctfassets.net/8pzh4g8cs30t/3myQWHijmnCYPA65bEs6Se/289c6cfa6f58288fb68ae21bc9dbb9b8/DC_Full_2x.png',
        urlLarge:
          'https://images.ctfassets.net/8pzh4g8cs30t/3myQWHijmnCYPA65bEs6Se/289c6cfa6f58288fb68ae21bc9dbb9b8/DC_Full_2x.png',
      },
    ],
  },
  [PRODUCT_SLUGS.bodyCleanse]: {
    name: 'Body Cleanse',
    thumbnailImage: [
      {
        fileName: 'Body_Rx_Cleanser_2x.jpg',
        url: 'https://images.ctfassets.net/8pzh4g8cs30t/2w0uInBjng08vYcLOLDFBU/4832034a6e1dd26c1a87bf61e1805a71/Body_Rx_Cleanser_2x.jpg',
        urlRaw:
          'https://images.ctfassets.net/8pzh4g8cs30t/2w0uInBjng08vYcLOLDFBU/4832034a6e1dd26c1a87bf61e1805a71/Body_Rx_Cleanser_2x.jpg',
        urlThumbnail:
          'https://images.ctfassets.net/8pzh4g8cs30t/2w0uInBjng08vYcLOLDFBU/4832034a6e1dd26c1a87bf61e1805a71/Body_Rx_Cleanser_2x.jpg',
        urlSmall:
          'https://images.ctfassets.net/8pzh4g8cs30t/2w0uInBjng08vYcLOLDFBU/4832034a6e1dd26c1a87bf61e1805a71/Body_Rx_Cleanser_2x.jpg',
        urlLarge:
          'https://images.ctfassets.net/8pzh4g8cs30t/2w0uInBjng08vYcLOLDFBU/4832034a6e1dd26c1a87bf61e1805a71/Body_Rx_Cleanser_2x.jpg',
      },
    ],
  },
  [PRODUCT_SLUGS.lipBalmOriginal]: {
    name: 'Lip Balm Original',
    thumbnailImage: [
      {
        fileName: 'LB_O_2x.png',
        url: 'https://images.ctfassets.net/8pzh4g8cs30t/4U4NDxdhLLsjhdwANZPpu1/3e4655c4e787f7ce9fb946314e50d968/LB_O_2x.png',
        urlRaw:
          'https://images.ctfassets.net/8pzh4g8cs30t/4U4NDxdhLLsjhdwANZPpu1/3e4655c4e787f7ce9fb946314e50d968/LB_O_2x.png',
        urlThumbnail:
          'https://images.ctfassets.net/8pzh4g8cs30t/4U4NDxdhLLsjhdwANZPpu1/3e4655c4e787f7ce9fb946314e50d968/LB_O_2x.png',
        urlSmall:
          'https://images.ctfassets.net/8pzh4g8cs30t/4U4NDxdhLLsjhdwANZPpu1/3e4655c4e787f7ce9fb946314e50d968/LB_O_2x.png',
        urlLarge:
          'https://images.ctfassets.net/8pzh4g8cs30t/4U4NDxdhLLsjhdwANZPpu1/3e4655c4e787f7ce9fb946314e50d968/LB_O_2x.png',
      },
    ],
  },
  [PRODUCT_SLUGS.lipBalmMint]: {
    name: 'Lip Balm Mint',
    thumbnailImage: [
      {
        fileName: 'LB_M_2x.png',
        url: 'https://images.ctfassets.net/8pzh4g8cs30t/1XB30742jck8RtaRnr4c32/d22015eb7485c01ea6b61c48fd988f02/LB_M_2x.png?h=250',
        urlRaw:
          'https://images.ctfassets.net/8pzh4g8cs30t/1XB30742jck8RtaRnr4c32/d22015eb7485c01ea6b61c48fd988f02/LB_M_2x.png?h=250',
        urlThumbnail:
          'https://images.ctfassets.net/8pzh4g8cs30t/1XB30742jck8RtaRnr4c32/d22015eb7485c01ea6b61c48fd988f02/LB_M_2x.png?h=250',
        urlSmall:
          'https://images.ctfassets.net/8pzh4g8cs30t/1XB30742jck8RtaRnr4c32/d22015eb7485c01ea6b61c48fd988f02/LB_M_2x.png?h=250',
        urlLarge:
          'https://images.ctfassets.net/8pzh4g8cs30t/1XB30742jck8RtaRnr4c32/d22015eb7485c01ea6b61c48fd988f02/LB_M_2x.png?h=250',
      },
    ],
  },
  [PRODUCT_SLUGS.lipBalmPassionFruit]: {
    name: 'Lip Balm Passion Fruit',
    thumbnailImage: [
      {
        fileName: 'LB_PF_2x.png',
        url: 'https://images.ctfassets.net/8pzh4g8cs30t/7slkgeJQ9nVO6Xf82rscKY/6446d72a469e54060c7de4f63ba01d60/LB_PF_2x.png',
        urlRaw:
          'https://images.ctfassets.net/8pzh4g8cs30t/7slkgeJQ9nVO6Xf82rscKY/6446d72a469e54060c7de4f63ba01d60/LB_PF_2x.png',
        urlThumbnail:
          'https://images.ctfassets.net/8pzh4g8cs30t/7slkgeJQ9nVO6Xf82rscKY/6446d72a469e54060c7de4f63ba01d60/LB_PF_2x.png',
        urlSmall:
          'https://images.ctfassets.net/8pzh4g8cs30t/7slkgeJQ9nVO6Xf82rscKY/6446d72a469e54060c7de4f63ba01d60/LB_PF_2x.png',
        urlLarge:
          'https://images.ctfassets.net/8pzh4g8cs30t/7slkgeJQ9nVO6Xf82rscKY/6446d72a469e54060c7de4f63ba01d60/LB_PF_2x.png',
      },
    ],
  },
  [PRODUCT_SLUGS.lipBalmVanilla]: {
    name: 'Lip Balm Vanilla',
    thumbnailImage: [
      {
        fileName: 'LB_V_2x.png',
        url: 'https://images.ctfassets.net/8pzh4g8cs30t/ipYLE9FFxEL1uP24aHY6i/3312074fb42f1b8bb8723d516d858093/LB_V_2x.png',
        urlRaw:
          'https://images.ctfassets.net/8pzh4g8cs30t/ipYLE9FFxEL1uP24aHY6i/3312074fb42f1b8bb8723d516d858093/LB_V_2x.png',
        urlThumbnail:
          'https://images.ctfassets.net/8pzh4g8cs30t/ipYLE9FFxEL1uP24aHY6i/3312074fb42f1b8bb8723d516d858093/LB_V_2x.png',
        urlSmall:
          'https://images.ctfassets.net/8pzh4g8cs30t/ipYLE9FFxEL1uP24aHY6i/3312074fb42f1b8bb8723d516d858093/LB_V_2x.png',
        urlLarge:
          'https://images.ctfassets.net/8pzh4g8cs30t/ipYLE9FFxEL1uP24aHY6i/3312074fb42f1b8bb8723d516d858093/LB_V_2x.png',
      },
    ],
  },
  [PRODUCT_SLUGS.lipBalmSpf]: {
    name: 'Lip Balm SPF',
    thumbnailImage: [
      {
        fileName: 'CLB_SPF_2x.png',
        url: 'https://images.ctfassets.net/8pzh4g8cs30t/MRLFjFeHC0ghm4BIKcmCj/84699c9c8b83e363ff11ebcd4224db8f/LB_SPF_2x.png?h=250',
        urlRaw:
          'https://images.ctfassets.net/8pzh4g8cs30t/MRLFjFeHC0ghm4BIKcmCj/84699c9c8b83e363ff11ebcd4224db8f/LB_SPF_2x.png?h=250',
        urlThumbnail:
          'https://images.ctfassets.net/8pzh4g8cs30t/MRLFjFeHC0ghm4BIKcmCj/84699c9c8b83e363ff11ebcd4224db8f/LB_SPF_2x.png?h=250',
        urlSmall:
          'https://images.ctfassets.net/8pzh4g8cs30t/MRLFjFeHC0ghm4BIKcmCj/84699c9c8b83e363ff11ebcd4224db8f/LB_SPF_2x.png?h=250',
        urlLarge:
          'https://images.ctfassets.net/8pzh4g8cs30t/MRLFjFeHC0ghm4BIKcmCj/84699c9c8b83e363ff11ebcd4224db8f/LB_SPF_2x.png?h=250',
      },
    ],
  },
};
